import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import { AppShell } from './shell';
import { Login } from './pages/Login';
import { ProjectsHome } from './pages/ProjectsHome';
import { Board } from './pages/Board';
import { ReportDetail } from './pages/ReportDetail';
import { ProjectSettings } from './pages/ProjectSettings';
import { Chat } from './pages/Chat';
import { Environments } from './pages/Environments';
import { TestReports } from './pages/TestReports';
import { Scenarios } from './pages/Scenarios';
import { AgentsList } from './pages/AgentsList';
import { AgentEditor } from './pages/AgentEditor';
import { Integrations } from './pages/Integrations';
import { DocsBrowser } from './pages/DocsBrowser';
import { Ephemeral } from './pages/Ephemeral';
import { Logs } from './pages/Logs';
import { Conversations } from './pages/Conversations';
import { Users } from './pages/Users';
import { AccountSettings } from './pages/AccountSettings';
import { SyncAppDownload } from './pages/SyncAppDownload';
import { Connect } from './pages/Connect';
import { ActiveAgents } from './pages/ActiveAgents';
import { Console } from './pages/Console';
import { Code } from './pages/Code';
import { Usage } from './pages/Usage';
import { DbAudit } from './pages/DbAudit';
import { AdminRoles } from './pages/AdminRoles';
import { AdminPipelineMd } from './pages/AdminPipelineMd';
import { SkillLibrary } from './pages/SkillLibrary';
import { NeoOnboarding } from './pages/NeoOnboarding';
import { NeoChat } from './pages/NeoChat';
import { NeoPlan } from './pages/NeoPlan';
import { Artifacts } from './pages/Artifacts';
import { AgentBases } from './pages/AgentBases';
import { OnboardingTypes } from './pages/OnboardingTypes';
import { Settings } from './pages/Settings';
import { Wiki } from './pages/Wiki';
import {
  AnalyticsStub,
} from './pages/stubs';

export function App() {
  const { me, loading } = useAuth();

  if (loading) return <div className="center">Carregando…</div>;

  if (!me) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<RedirectToLogin />} />
      </Routes>
    );
  }

  // A wiki é standalone (fora do AppShell): a documentação aberta como site próprio.
  return (
    <Routes>
      <Route path="/wiki/:slug" element={<Wiki />} />
      <Route path="*" element={<Shell />} />
    </Routes>
  );
}

// Não autenticado: manda pro login preservando o destino pretendido em ?next=.
// (a home "/" não precisa de next — o login já cai nela por padrão)
function RedirectToLogin() {
  const loc = useLocation();
  const target = loc.pathname + loc.search;
  const to = target && target !== '/' ? `/login?next=${encodeURIComponent(target)}` : '/login';
  return <Navigate to={to} replace />;
}

function Shell() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<ProjectsHome />} />
        <Route path="/connect" element={<Connect />} />
        <Route path="/account/tokens" element={<AccountSettings />} />
        {/* Configurações — hub com abas (agrupa admin + conta). */}
        <Route path="/settings" element={<Settings />}>
          <Route path="users" element={<Users />} />
          <Route path="roles" element={<AdminRoles />} />
          <Route path="sync-app" element={<SyncAppDownload />} />
          <Route path="console" element={<Console />} />
          <Route path="usage" element={<Usage />} />
          <Route path="db-audit" element={<DbAudit />} />
          <Route path="agents" element={<ActiveAgents />} />
          <Route path="pipeline-md" element={<AdminPipelineMd />} />
          <Route path="agent-bases" element={<AgentBases />} />
          <Route path="onboarding-types" element={<OnboardingTypes />} />
        </Route>
        {/* Redirecionamentos das rotas antigas → Configurações. */}
        <Route path="/account/sync-app" element={<Navigate to="/settings/sync-app" replace />} />
        <Route path="/users" element={<Navigate to="/settings/users" replace />} />
        <Route path="/admin/agents" element={<Navigate to="/settings/agents" replace />} />
        <Route path="/admin/usage" element={<Navigate to="/settings/usage" replace />} />
        <Route path="/admin/db-audit" element={<Navigate to="/settings/db-audit" replace />} />
        <Route path="/admin/roles" element={<Navigate to="/settings/roles" replace />} />
        <Route path="/admin/pipeline-md" element={<Navigate to="/settings/pipeline-md" replace />} />
        <Route path="/skills" element={<SkillLibrary />} />
        <Route path="/neo" element={<NeoOnboarding />} />
        <Route path="/console" element={<Navigate to="/settings/console" replace />} />
        <Route path="/p/:slug" element={<Board />} />
        <Route path="/p/:slug/neo" element={<NeoChat />} />
        <Route path="/p/:slug/plano" element={<NeoPlan />} />
        <Route path="/p/:slug/artifacts" element={<Artifacts />} />
        <Route path="/p/:slug/chat" element={<Chat />} />
        <Route path="/p/:slug/chat/:threadId" element={<Chat />} />
        <Route path="/p/:slug/conversations" element={<Conversations />} />
        <Route path="/p/:slug/settings" element={<ProjectSettings />} />
        <Route path="/p/:slug/environments" element={<Environments />} />
        <Route path="/p/:slug/ephemeral" element={<Ephemeral />} />
        <Route path="/p/:slug/logs" element={<Logs />} />
        <Route path="/p/:slug/test-reports" element={<TestReports />} />
        <Route path="/p/:slug/scenarios" element={<Scenarios />} />
        <Route path="/p/:slug/code" element={<Code />} />
        <Route path="/p/:slug/console" element={<Console />} />
        <Route path="/p/:slug/agents" element={<AgentsList />} />
        <Route path="/p/:slug/agents/:agentId" element={<AgentEditor />} />
        <Route path="/p/:slug/integrations" element={<Integrations />} />
        <Route path="/p/:slug/knowledge" element={<DocsBrowser />} />
        <Route path="/p/:slug/analytics" element={<AnalyticsStub />} />
        <Route path="/p/:slug/r/:reportId" element={<ReportDetail />} />
        {/* Legado: /chat global redireciona pra home (escolher projeto primeiro). */}
        <Route path="/chat" element={<Navigate to="/" replace />} />
        <Route path="/chat/:threadId" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
